import { IconPlus } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { canAdminWrite, roleSettled } from '../lib/roles';
import { SESSION_TOKEN, useMe } from '../lib/session';
import { createSquadEditor } from '../lib/squad-editor';
import { implicitCount, namedSquads, type SquadWire, useSquads } from '../lib/squads';
import { createToast } from '../lib/toast';
import { ConfirmDialog } from './ConfirmDialog';
import { EmptyState } from './EmptyState';
import { PageHeader } from './PageHeader';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';
import { SquadEditModal } from './SquadEditModal';
import { SquadTable } from './SquadTable';
import { Toast } from './Toast';
import { Button } from './ui/Button';

// Squad settings (#6780) — admins name groups of the account's repos, which Fleet Live's
// Core Intelligence groups by (#6779). CONTAINER: it owns the one `GET /v1/squads` read,
// the role gate and the editor; the table, the modal and the picker render.
//
// WHO WRITES: `write:squads`, an ADMIN-ceiling scope (`ADMIN_SCOPES`, #7010), so
// `canAdminWrite` on a SETTLED role (`roleSettled`, never a prior org's placeholder).
// Members and viewers see the table and a line saying who can change it. The server stays
// the authority: a credential the role check lets through but the scope refuses gets the
// route's 403 `detail` in the modal, and nothing is saved.

/** The line under the table: how many repos still stand as their own squad. */
function implicitLine(rows: readonly SquadWire[]): string {
  const n = implicitCount(rows);
  if (n === 0) return t('squads.all_grouped');
  return tPlural('squads.implicit_count', n, { count: n });
}

export function SquadSettings(props: { readonly token?: string | null }): JSX.Element {
  // `=== undefined`, not `??`: an injected `null` MEANS "no session".
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const me = useMe(token);
  const squads = useSquads(token);
  const canWrite = (): boolean => roleSettled(me) && canAdminWrite(me.data?.role);
  const isReader = (): boolean => roleSettled(me) && !canAdminWrite(me.data?.role);
  const { toast, notify, dismiss } = createToast();
  const editor = createSquadEditor({ token, reread: () => squads.refetch(), notify });
  const asked = editor.deleting.pending;

  return (
    <>
      <PageHeader
        title={t('squads.title')}
        desc={t('squads.subtitle')}
        actions={
          <Show when={token !== null && canWrite()}>
            <Button onClick={() => editor.open()}>
              <IconPlus />
              {t('squads.new')}
            </Button>
          </Show>
        }
      />

      <Show when={token !== null} fallback={<EmptyState message={t('squads.signed_out')} />}>
        <Show when={isReader()}>
          <p class="squads__readers-note">{t('squads.readers_note')}</p>
        </Show>
        {/* QueryBoundary, not a bare Show: loading must not read as "empty". */}
        <QueryBoundary
          query={squads}
          isEmpty={(rows) => namedSquads(rows).length === 0}
          loading={<Skeleton height="12rem" />}
          empty={<EmptyState message={t('squads.empty')} />}
        >
          {(rows) => (
            <div class="squads__list">
              <SquadTable
                rows={namedSquads(rows)}
                onEdit={canWrite() ? editor.open : undefined}
                onDelete={canWrite() ? editor.deleting.ask : undefined}
              />
              <p class="squads__implicit">{implicitLine(rows)}</p>
            </div>
          )}
        </QueryBoundary>
      </Show>

      <SquadEditModal
        target={editor.editing()}
        rows={squads.data ?? []}
        pending={editor.pending()}
        nameError={editor.nameError()}
        formError={editor.formError()}
        onSave={editor.submit}
        onClose={editor.close}
      />
      <ConfirmDialog
        open={asked() !== null}
        title={t('squads.delete_title', { name: asked()?.name ?? '' })}
        desc={t('squads.delete_desc')}
        cancelLabel={t('squads.cancel')}
        actionLabel={t('squads.delete_confirm')}
        destructive
        onCancel={editor.deleting.cancel}
        onConfirm={() => void editor.deleting.confirm()}
      />
      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'success'}
        onDismiss={dismiss}
      />
    </>
  );
}
